import { useCallback, useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import QRCode from "react-fancy-qrcode";
import { LanguageContext } from "../../../context/LanguageContext";
import { ParagraphText } from "../../../Libraries";
import BusinessLoader from "../../common/BusinessLoader";
import NonIdealNew from "../../common/NonIdealNew";
import { handleApi } from "../../../apis";
import { GET_QR_DETAILS } from "../../../apis/configuration";
import { CopyIconNew, RupinFavIcon } from "../../../assets/icons/icon";
import { useSnackbar } from "../../../context/SnackbarContext";
import LogoRupin from "../../../assets/icons/Logo Rupin.svg";
import styles from "../../common/ShowQR/styles.module.css";
import { InnerWrapper, MainContainer } from "../../common";

const PublicQRView = () => {
  const [I18n] = useContext(LanguageContext);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const { showSnackbar } = useSnackbar();

  const fetchQrDetails = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await handleApi(
        GET_QR_DETAILS,
        { qr_share_key: id },
        null,
        null,
      );
      setQrData(response?.raast_details);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQrDetails();
  }, [fetchQrDetails]);

  if (!id) return <Navigate replace to="/not-found" />;

  if (isLoading) return <BusinessLoader loadBusiness />;

  if (hasError) {
    return (
      <NonIdealNew
        title={errorMessage || I18n.something_went_wrong}
        subtitle={I18n.could_not_load_qr}
        button1Title={I18n.try_again}
        onButton1Click={fetchQrDetails}
      />
    );
  }

  const qrCode = qrData?.qr_code || "";
  const accountTitle = qrData?.name || "";
  const iban = qrData?.iban || "";
  const accountNumber = qrData?.account_number || "";

  const handleCopy = async () => {
    const details = [accountTitle, iban, accountNumber].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(details);
      setIsCopied(true);
      showSnackbar({ message: I18n.copied, severity: "success", icon: "" });
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      showSnackbar({ message: I18n.copy_failed || "Failed to copy", severity: "error", icon: "" });
    }
  };

  return (
    <MainContainer className="walletOnboardingA" fluid>
      <Grid container>
        <Grid item md={12}>
          <InnerWrapper className="innerWrapper newInnerWrapper publicQrView">
            <Box className={styles.showQrContent}>
              <Box className={styles.qrSection}>
                <Box className={`${styles.qrCodeBox} qrContrastSurface`}>
                  {qrCode ? (
                    <QRCode
                      value={qrCode}
                      size={202}
                      dotRadius="12%"
                      dotScale={1}
                      positionRadius={5}
                      logo={LogoRupin}
                    />
                  ) : (
                    <CircularProgress size={32} />
                  )}
                </Box>
              </Box>

              <Box className={styles.accountCard}>
                <RupinFavIcon />
                <Box className={styles.accountCardInfo}>
                  <ParagraphText className={styles.accountName}>
                    {accountTitle}
                  </ParagraphText>
                  <Box className={styles.accountIbanRow}>
                    <ParagraphText className={styles.accountIban}>{iban}</ParagraphText>
                    <button
                      type="button"
                      className={styles.copyIconBtn}
                      onClick={handleCopy}
                      aria-label={I18n.copy}
                    >
                      <CopyIconNew width={18} height={18} />
                    </button>
                  </Box>
                  {accountNumber ? (
                    <ParagraphText
                      className="fontSize13 newLightColor"
                      style={{ margin: 0 }}
                    >
                      {accountNumber}
                    </ParagraphText>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default PublicQRView;
